import { logService } from '@nsftx/seven-client-retail-utils'
import {
  getBetslipLogDetails,
  useTicketsStore,
  TicketResolveError,
} from '@nsftx/seven-client-retail-vue-sgp'
import type { BetslipView } from '@nsftx/seven-client-retail-vue-integrator'
import { useNotificationsStore } from '@nsftx/seven-nxg-retail-vue-sdk'
import errorParser from '@/utils/errorParser'
import { t } from '@/plugins/translationsPlugin'
import type { SlipCheckerOptions } from './interfaces/SlipCheckerOptions'

const SLIP_RESOLVE_STATUS_TIMEOUT_IN_MS = 5_000
export const SLIP_ACTION_PENDING_NOTIFICATION_ID = 'notification_slip_action_pending'
const LOG_PREFIX = '[slipCheckerService]'

const waitForTicketResolve = (feBetslipId: string) =>
  new Promise<BetslipView>((resolve, reject) => {
    const { isTicketResolved } = useTicketsStore()

    window.setTimeout(() => {
      const resolvedTicket = isTicketResolved(feBetslipId)

      if (resolvedTicket && resolvedTicket.betslip) {
        logService.debug(
          `${LOG_PREFIX} Slip already resolved by socket, skipping slip checker result processing.`,
          {
            code: 'SS_SLIP_CHECKER_ALREADY_RESOLVED',
            ...getBetslipLogDetails(resolvedTicket.betslip),
          },
        )
        resolve(resolvedTicket.betslip)
        return
      }

      reject(
        new TicketResolveError(
          `Slip is not resolved by socket within ${SLIP_RESOLVE_STATUS_TIMEOUT_IN_MS} ms.`,
          'SGP_TICKET_RESOLVE_SOCKET_TIMEOUT_ERROR',
          {
            context: {
              feBetslipId,
            },
          },
        ),
      )
    }, SLIP_RESOLVE_STATUS_TIMEOUT_IN_MS)
  })

const startSlipChecker = async (
  feBetslipId: string,
  onSuccess: SlipCheckerOptions['onResolveSuccess'],
  onFailed: SlipCheckerOptions['onResolveFailed'],
) => {
  const { acceptTicket, setFailedTicket, startChecker, endChecker, isActionPending } =
    useTicketsStore()

  logService.debug(`${LOG_PREFIX} Slip checker started.`, {
    code: 'SS_SLIP_CHECKER_START_SUCCESS',
    feBetslipId,
  })

  try {
    const betslip = await startChecker(feBetslipId)
    const betslipStatusPhase = betslip.status.phase

    if (!isActionPending(feBetslipId)) {
      logService.debug(`${LOG_PREFIX} Slip already resolved by socket, skipping slip checker.`, {
        code: 'SS_SLIP_CHECKER_ALREADY_RESOLVED',
        ...getBetslipLogDetails(betslip),
      })
      return
    }

    logService.info(`${LOG_PREFIX} Slip successfully resolved by slip checker.`, {
      code: 'SS_SLIP_CHECKER_RESOLVE_SUCCESS',
      ...getBetslipLogDetails(betslip),
      status: betslipStatusPhase,
    })

    if (betslipStatusPhase !== 'PLACED') {
      setFailedTicket(feBetslipId)
      endChecker(feBetslipId)
      return
    }

    acceptTicket(feBetslipId, betslip)
    endChecker(feBetslipId)
    onSuccess(betslip)
  } catch (error) {
    const { showNotification } = useNotificationsStore()

    logService.error(`${LOG_PREFIX} Slip resolve has failed.`, {
      code: 'SS_SLIP_RESOLVE_CHECKER_ERROR',
      ...errorParser.parseUpstream(error),
    })
    showNotification({
      variant: 'error',
      text: [t('slip_resolve_failed')],
    })

    setFailedTicket(feBetslipId)
    endChecker(feBetslipId)
    onFailed(error)
  }
}

const handleSlipChecker = async (options: SlipCheckerOptions) => {
  const { showNotification } = useNotificationsStore()
  const { activeFeBetslipId, onResolveSuccess, onResolveFailed } = options

  showNotification({
    id: SLIP_ACTION_PENDING_NOTIFICATION_ID,
    variant: 'info',
    text: [t('slip_pending_action')],
  })

  try {
    await waitForTicketResolve(activeFeBetslipId)
  } catch (error) {
    logService.error(`${LOG_PREFIX} Slip socket resolve has failed.`, {
      code: 'SS_SLIP_RESOLVE_SOCKET_ERROR',
      ...errorParser.parseUpstream(error),
    })
    startSlipChecker(activeFeBetslipId, onResolveSuccess, onResolveFailed).catch(() => {
      showNotification({
        variant: 'error',
        text: [t('slip_resolve_failed')],
      })
    })
  }
}

export default {
  handleSlipChecker,
}
